import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { splitAnnotatedElementsAtOffset } from 'components/WorkspaceEditor/utils/splitAnnotatedElementsAtOffset'
import { TrackChangeType } from 'models/changes'
import { PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT as NODE_TYPES } from 'pages/ProjectsPage/WorkspacePage/components/constants'
import { v4 as newGuid } from 'uuid'
import type { ContentItemViewNodesType } from '../../../types/contentItemViewNode.type'

const DEFAULT_TEXT_AFTER_UOM = ' '
const isWhitespaceOnly = (text: string): boolean =>
    /^[\u200B\uFEFF\s]*$/.test(text)

const makeTextEl = (
    id: string,
    text: string,
    base?: Record<string, unknown>
) => ({
    ...base,
    id,
    type: NODE_TYPES.Text,
    text,
    elements: [],
    imperialUnits: '',
    metricUnits: '',
    placeholderText: '',
})

const makeUOMEl = (id: string, authoredUserId: string) => ({
    id,
    type: NODE_TYPES.UnitsOfMeasure,
    text: '',
    elements: [],
    imperialUnits: '',
    metricUnits: '',
    changeType: TrackChangeType.ADDED,
    placeholderText: '',
    authoredUserId,
})

const wrapUOMInSelectionOption = (
    uomEl: Partial<TextContentElement>,
    authoredUserId: string
): Partial<TextContentElement> => ({
    id: newGuid(),
    type: NODE_TYPES.SelectionOption,
    text: '',
    changeType: TrackChangeType.ADDED,
    authoredUserId,
    optionItemState: null,
    placeholderText: '',
    imperialUnits: '',
    metricUnits: '',
    elements: [makeTextEl(newGuid(), ''), uomEl, makeTextEl(newGuid(), '')],
})

/**
 * Scans a flat elements array and groups adjacent standalone UnitsOfMeasure
 * elements (separated by whitespace-only text) into a single OptionGroup.
 * Called as an optimistic update when saving a standalone UOM.
 */
export const groupAdjacentUOMsInElements = (
    elements: Partial<TextContentElement>[],
    authoredUserId: string
): Partial<TextContentElement>[] => {
    const result: Partial<TextContentElement>[] = []
    let i = 0
    while (i < elements.length) {
        const el = elements[i]
        if (el.type !== NODE_TYPES.UnitsOfMeasure) {
            result.push(el)
            i++
            continue
        }
        // Collect this UOM and any directly adjacent UOMs (whitespace-only text between them)
        const group: Partial<TextContentElement>[] = [el]
        let j = i + 1
        while (j < elements.length) {
            const peek = elements[j]
            if (
                peek.type === NODE_TYPES.Text &&
                isWhitespaceOnly((peek.text as string) ?? '')
            ) {
                const next = j + 1 < elements.length ? elements[j + 1] : null
                if (next?.type === NODE_TYPES.UnitsOfMeasure) {
                    j++ // advance past the whitespace
                    group.push(elements[j])
                    j++
                } else {
                    break
                }
            } else if (peek.type === NODE_TYPES.UnitsOfMeasure) {
                group.push(peek)
                j++
            } else {
                break
            }
        }
        if (group.length === 1) {
            result.push(el)
            i++
        } else {
            const uomElements = group.filter(
                (el) => el.type === NODE_TYPES.UnitsOfMeasure
            )
            const groupChangeType = uomElements.some(
                (el) => el.changeType === TrackChangeType.ADDED
            )
                ? TrackChangeType.ADDED
                : TrackChangeType.MODIFIED
            result.push({
                id: newGuid(),
                type: NODE_TYPES.OptionGroup,
                text: '',
                changeType: groupChangeType,
                optionItemState: null,
                elements: uomElements.map((uom) =>
                    wrapUOMInSelectionOption(uom, authoredUserId)
                ),
            })
            i = j
        }
    }
    return result
}

/** Strips UOM-irrelevant fields from a TextContentElement, returning a plain base object. */
export const buildTextElBase = (
    textEl: TextContentElement & { authoredUserId?: string }
): Record<string, unknown> => {
    const {
        optionItemState: _ois,
        changeType: _ct,
        authoredUserId: _aui,
        placeholderText: _pt,
        ...rest
    } = textEl
    return rest as Record<string, unknown>
}

export interface BuildNodesWithInsertedUOMParams {
    currentNodes: Partial<ContentItemViewNodesType>[]
    paragraphNode: Partial<ContentItemViewNodesType> | null
    elementId: string
    textElBase: Record<string, unknown>
    offset: number
    annotated: Array<{ text?: string | null }>
    newUOMId: string
    authoredUserId: string
}

/**
 * Pure tree transform: inserts a standalone UnitsOfMeasure node at the cursor position.
 * Adjacent UOMs are grouped into an OptionGroup as an optimistic update on save.
 *
 * Returns null for invalid contexts (cursor inside a nested element or on an OptionGroup).
 * On success, also returns newParagraphElements — the replacement slice for the original
 * text element — so callers can splice it into server nodes on postGet rather than
 * appending the entire paragraph element array (which would duplicate the saved text).
 */
export const buildNodesWithInsertedUOM = ({
    currentNodes,
    paragraphNode,
    elementId,
    textElBase,
    offset,
    annotated,
    newUOMId,
    authoredUserId,
}: BuildNodesWithInsertedUOMParams):
    | null
    | {
          nodes: Partial<ContentItemViewNodesType>[]
          newParagraphElements: Record<string, unknown>[]
      } => {
    const paragraphElements = (paragraphNode?.elements ?? []) as Record<
        string,
        unknown
    >[]
    const currentIdx = paragraphElements.findIndex((el) => el.id === elementId)

    // Cursor is inside a nested element (e.g. SelectionOption text) — not a valid context.
    if (currentIdx === -1) return null

    // Guard: don't insert into an OptionGroup element position.
    if (paragraphElements[currentIdx]?.type === NODE_TYPES.OptionGroup)
        return null

    const {
        before: beforeEls,
        after: afterEls,
    } = splitAnnotatedElementsAtOffset(annotated, offset)

    const mappedBefore = (beforeEls.length > 0
        ? beforeEls.map((el, i) => ({
              ...textElBase,
              ...el,
              id: i === 0 ? elementId : newGuid(),
              type: NODE_TYPES.Text,
              text: el.text || '',
          }))
        : [makeTextEl(elementId, '', textElBase)]) as Record<string, unknown>[]

    const mappedAfter = (afterEls.length > 0
        ? afterEls.map((el) => ({
              ...el,
              id: newGuid(),
              type: NODE_TYPES.Text,
          }))
        : [makeTextEl(newGuid(), DEFAULT_TEXT_AFTER_UOM)]) as Record<
        string,
        unknown
    >[]

    const newParagraphElements = [
        ...mappedBefore,
        makeUOMEl(newUOMId, authoredUserId),
        ...mappedAfter,
    ]

    const nodes = currentNodes.map((node) => {
        const idx =
            (node.elements as Record<string, unknown>[] | undefined)?.findIndex(
                (el) => el.id === elementId
            ) ?? -1
        if (idx === -1) return node
        return {
            ...node,
            elements: [
                ...((node.elements as Record<string, unknown>[]) ?? []).slice(
                    0,
                    idx
                ),
                ...newParagraphElements,
                ...((node.elements as Record<string, unknown>[]) ?? []).slice(
                    idx + 1
                ),
            ],
        }
    }) as Partial<ContentItemViewNodesType>[]

    return { nodes, newParagraphElements }
}
